import React from 'react';
import { IconDelete, IconMove } from '@punchcard/core/icons';
import ModalOverlay from './ModalOverlay';
import { ControllerRenderProps } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button } from './Button';
import { Tooltip } from 'react-tooltip';
import classNames from 'classnames';
import { usePDFReaderContext } from './PDFReaderContext';
import { WIPMode } from '@punchcard/core/constants/Wip';

interface IProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
	field: ControllerRenderProps<WIPForm, `studentLayer.${number}`>;
	line: Line;
	pageIndex: number;
}
const LineContainer = (props: IProps) => {
	const { field, line, pageIndex } = props;
	const { activeLine, setActiveLine, mode } = usePDFReaderContext();
	const isActiveLine = activeLine?.id === line.id;
	const { t } = useTranslation();
	const componentRef = React.useRef<HTMLDivElement>(null);
	const MIN_LINE_WIDTH = 30;
	const pdfWindow = document.querySelector('.react-pdf__Document');
	const pdfWindowWidth = (pdfWindow?.clientWidth || 0) - 5;
	const pdfWindowHeight = (pdfWindow?.clientHeight || 0) - 5;
	const lineRef = React.useRef<HTMLElement | null>(null);
	type ResizeRef = {
		startX: number;
		startY: number;
		endX: number;
		endY: number;
		initialWidth: number;
	};
	const initialPositionRef = React.useRef({ startX: 0, startY: 0, initialLeft: 0, initialTop: 0 });
	const initialRightWidthRef = React.useRef<ResizeRef>({
		startX: 0,
		startY: 0,
		endX: 0,
		endY: 0,
		initialWidth: 0,
	});
	const initialLeftWidthRef = React.useRef<ResizeRef>({
		startX: 0,
		startY: 0,
		endX: 0,
		endY: 0,
		initialWidth: 0,
	});
	const [showDeleteLineModal, setShowDeleteLineModal] = React.useState<boolean>(false);
	const enterTimeoutRef = React.useRef<number | null>(null);
	const handleHeight = 12;
	const calculateWidth = (startX: number, startY: number, endX: number, endY: number) => {
		const length = Math.sqrt((endX - startX) ** 2 + (endY - startY) ** 2);
		return length;
	};
	const calculateAngle = (startX: number, startY: number, endX: number, endY: number) => {
		const angle = Math.atan2(endY - startY, endX - startX) * (180 / Math.PI);
		return angle;
	};

	const styles = {
		container: {
			top: line.startY,
			left: line.startX,
			bottom: line.endY,
			right: line.endX,
			width: `${calculateWidth(line.startX, line.startY, line.endX, line.endY)}px`,
			transformOrigin: line.transformOrigin,
			transform: `rotate(${calculateAngle(line.startX, line.startY, line.endX, line.endY)}deg)`,
			touchAction: 'none',
		},
		buttons: {
			top: Math.max(50, Math.min(Math.min(line.startY, line.endY) - 60, pdfWindowHeight - 60)),
			left: Math.max(0, Math.min((line.startY < line.endY ? line.startX : line.endX) - 50, pdfWindowWidth - 100)),
			zIndex: 1501,
			touchAction: 'none',
		}
	};
	const handleDeleteLineModal = () => {
		setShowDeleteLineModal(true);
	};
	const handleDeleteLine = (field: ControllerRenderProps<WIPForm, `studentLayer.${number}`>) => {
		const updatedLines = field.value.lines?.filter((t: { id: string }) => t.id !== line.id);
		field.onChange({ ...field.value, lines: updatedLines });
		setShowDeleteLineModal(false);  // Close the modal after deletion
	};

	React.useEffect(() => {
		setTimeout(() => {
			if (activeLine && lineRef.current) {
				lineRef.current.focus();
			}
		}, 50);
	}, [activeLine]);

	React.useEffect(() => {
		if (mode !== WIPMode.Line) {
			setActiveLine(null);
		}
	}, [mode, setActiveLine]);

	// ======== Resize Handles =========
	const setupResize = (
		event: React.PointerEvent,
		side: 'left' | 'right'
	) => {
		const targetElement = event.currentTarget.closest('.divLineArea') as HTMLElement;
		const isLeft = side === 'left';

		const ref = isLeft ? initialLeftWidthRef : initialRightWidthRef;
		ref.current[isLeft ? 'startX' : 'endX'] = event.clientX;
		ref.current[isLeft ? 'startY' : 'endY'] = event.clientY;
		line.transformOrigin = 'left';
		ref.current.initialWidth = parseFloat(targetElement.style.width || '0');

		document.body.style.userSelect = 'none';
		const closestDivLine = targetElement.closest('.divLine');
		if (closestDivLine) {
			closestDivLine.classList.add('resizing');
		}
		const moveHandler = (e: PointerEvent) => {
			const target = e.target as HTMLElement;
			if (!target) { return; }
			const divPage = target.closest('.divPage') as HTMLElement;
			if (!divPage) { return; }

			const lineArray = field.value.lines || [];
			const deltaX = e.clientX - ref.current[isLeft ? 'startX' : 'endX'];
			const deltaY = e.clientY - ref.current[isLeft ? 'startY' : 'endY'];

			const updatedCoord = {
				startX: line.startX + deltaX,
				startY: line.startY + deltaY,
				endX: line.endX + deltaX,
				endY: line.endY + deltaY,
			};

			const targetIndex = lineArray.findIndex((t: { id: string }) => t.id === line.id);
			const hasValidIndex = targetIndex !== -1;

			const hasMinWidth = isLeft
				? Math.abs(updatedCoord.startX - line.endX) > MIN_LINE_WIDTH ||
				Math.abs(updatedCoord.startY - line.endY) > MIN_LINE_WIDTH
				: Math.abs(updatedCoord.endX - line.startX) > MIN_LINE_WIDTH ||
				Math.abs(updatedCoord.endY - line.startY) > MIN_LINE_WIDTH;

			const withinHorizontalBounds =
				(isLeft ? updatedCoord.startX : updatedCoord.endX) >= 0 &&
				(isLeft ? updatedCoord.startX : updatedCoord.endX) <= pdfWindowWidth;

			const updatedY = isLeft ? updatedCoord.startY : updatedCoord.endY;
			const pageTop = divPage.offsetTop;
			const pageBottom = divPage.offsetTop + divPage.clientHeight;
			const withinVerticalBounds = !(
				(updatedY - handleHeight < pageTop) ||
				(updatedY + handleHeight > pageBottom)
			);
			const isValid = hasValidIndex && hasMinWidth && withinHorizontalBounds && withinVerticalBounds;

			if (isValid) {
				const updatedLines = lineArray.map((t: { id: string }) =>
					t.id === line.id
						? {
							...t,
							...(isLeft
								? { startX: updatedCoord.startX, startY: updatedCoord.startY }
								: { endX: updatedCoord.endX, endY: updatedCoord.endY }),
						}
						: t
				);
				field.onChange({ ...field.value, lines: updatedLines });
			}
		};

		const upHandler = () => {
			document.body.style.userSelect = '';
			if (closestDivLine) {
				closestDivLine.classList.remove('resizing');
			}
			document.removeEventListener('pointermove', moveHandler);
			document.removeEventListener('pointerup', upHandler);
		};

		document.addEventListener('pointermove', moveHandler);
		document.addEventListener('pointerup', upHandler);
	};

	const handleResizeDown = (e: React.PointerEvent, side: 'left' | 'right') => {
		(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
		setupResize(e, side);
	};

	const handleMouseDown = (event: React.PointerEvent) => {
		if (mode !== WIPMode.Line) {
			return;
		}
		const target = event.target as HTMLElement;

		// Prevent dragging when interacting with resize handles
		const isResizeHandle = target.classList.contains('drag-handle');
		if (isResizeHandle) { return; }

		const targetElement = (event.currentTarget as HTMLElement)
			.closest('.divLine') as HTMLElement;
		if (!targetElement) { return; }

		event.currentTarget.setPointerCapture(event.pointerId);

		const { clientX, clientY } = event;

		initialPositionRef.current = {
			startX: clientX,
			startY: clientY,
			initialLeft: parseFloat(targetElement.style.left || '0'),
			initialTop: parseFloat(targetElement.style.top || '0'),
		};

		targetElement.closest('.react-pdf__Document')?.classList.add('dragging');

		document.addEventListener('pointermove', handleMouseMove);
		document.addEventListener('pointerup', handleMouseUp);
	};
	const handleMouseMove = (event: PointerEvent) => {
		const targetElement = event.target as HTMLElement;
		if (!targetElement) { return; }

		const { startX, startY } = initialPositionRef.current;
		const deltaX = event.clientX - startX;
		const deltaY = event.clientY - startY;

		const newStartX = line.startX + deltaX;
		const newStartY = line.startY + deltaY;
		const newEndX = line.endX + deltaX;
		const newEndY = line.endY + deltaY;

		const lineArray = field.value.lines || [];
		const targetIndex = lineArray.findIndex((t: { id: string }) => t.id === line.id);
		const divPage = targetElement.closest('.divPage') as HTMLElement;

		const withinHorizontalBounds = newStartX >= 0 && newEndX <= pdfWindowWidth;
		const withinVerticalBounds =
			!((newStartY - handleHeight < 0 || newEndY - handleHeight < 0)) &&
			!((newStartY + handleHeight > divPage?.clientHeight || newEndY + handleHeight > divPage?.clientHeight));

		if (divPage && targetIndex !== -1 && withinHorizontalBounds && withinVerticalBounds) {
			const updatedLines = lineArray.map((t: { id: string }) =>
				t.id === line.id
					? { ...t, startX: newStartX, startY: newStartY, endX: newEndX, endY: newEndY }
					: t
			);
			field.onChange({ ...field.value, lines: updatedLines });
		}
	};

	// Stop dragging
	const handleMouseUp = (e: PointerEvent) => {
		document.removeEventListener('pointermove', handleMouseMove);
		document.removeEventListener('pointerup', handleMouseUp);

		const targetElement = e.target as HTMLElement;
		if (!targetElement) { return; }
		const closestDivLine = targetElement.closest('.react-pdf__Document') as HTMLElement;
		if (closestDivLine) {
			closestDivLine.classList.remove('dragging');
		}
	};

	React.useEffect(() => {
		function handleClickOutside(event: MouseEvent) {
			const target = event.target as HTMLElement;
			if (target.closest('.modal.show')) {
				return; // Exit if clicked inside a visible modal
			}

			if (!target.id.startsWith('canvas-')) {
				return; // Exit if clicked inside the canvas
			}
			if (componentRef &&
				componentRef.current &&
				!componentRef.current.contains(event.target as Node)
			) {
				setActiveLine(null);
				setShowDeleteLineModal(false);
			}
		}
		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [setActiveLine]);

	const handleHover = (e: React.MouseEvent, direction: 'enter' | 'leave') => {
		if (activeLine?.id === line.id) {
			return;
		}

		if (mode === WIPMode.Line && activeLine?.id !== line.id) {
			e.stopPropagation();
			if (direction === 'enter') {
				enterTimeoutRef.current = window.setTimeout(() => {
					setActiveLine({ page: pageIndex, id: line.id });
				}, 100);
			} else if (direction === 'leave') {
				if (enterTimeoutRef.current !== null) {
					clearTimeout(enterTimeoutRef.current);
				}
				setActiveLine(null);
			}
		} else {
			e.preventDefault();
		}
	};

	return (
		<div className={classNames('divLine', { 'active': isActiveLine })}>
			<Tooltip id="linetool-tooltip" place="top" className="tooltip" delayHide={1000000} />
			<div className="border-secondary-500 bg-secondary-100 divButtons" style={styles.buttons as React.CSSProperties}>
				<Button
					onPointerDown={(e) => { e.preventDefault(); handleMouseDown(e); }}
					className="btn-ghost-primary-300 p-2 btn-blue-hover"
					data-tooltip-id="linetool-tooltip" data-tooltip-content="Move" data-tooltip-place="top"
				>
					<IconMove width="10px" />
				</Button>
				<Button
					onClick={() => handleDeleteLineModal()}
					className="btn-ghost-primary-300 p-2 btn-blue-hover"
					data-tooltip-id="linetool-tooltip" data-tooltip-content="Delete" data-tooltip-place="top"
				>
					<IconDelete width="10px" />
				</Button>
			</div>

			<div
				ref={componentRef}
				className="divLineArea lineContainer"
				style={styles.container}
				onPointerDown={(e) => { e.preventDefault(); handleMouseDown(e); }}
				role={isActiveLine ? 'button' : undefined}
				tabIndex={isActiveLine ? 0 : undefined}
				aria-label="Move or resize line"
				onPointerEnter={(e) => handleHover(e, 'enter')}
				onPointerLeave={(e) => handleHover(e, 'leave')}
			>
				<button
					className="drag-handle"
					onPointerDown={(e) => handleResizeDown(e, 'left')}
				>
					<div className="border border-secondary-500 handleBox cursor-grab" />
				</button>
				<div className="line" />
				<button
					className="drag-handle"
					onPointerDown={(e) => handleResizeDown(e, 'right')}
				>
					<div className="border border-secondary-500 handleBox cursor-grab" />
				</button>
			</div>
			{isActiveLine && (
				<ModalOverlay
					isOpen={showDeleteLineModal}
					modalSize="md"
					onRequestClose={() => setShowDeleteLineModal(false)}
					headerChildren={t('wip.delete_line')}
					confirmButtonChildren={t('wip.confirm')}
					cancelButtonChildren={t('wip.cancel')}
					hideCloseButton={true}
					confirmButtonClassName="btn-danger bg-danger-500"
					shouldCloseOnOverlayClick={false}
					confirmButtonAction={() => {
						handleDeleteLine(field);
					}}
					cancelButtonAction={() => {
						setShowDeleteLineModal(false);
					}}
				>
					{t('wip.are_you_sure_you_want_to_delete_this_line')}
				</ModalOverlay>
			)}
		</div>
	);
};

export default LineContainer;
